import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

/**
 * Formatting helpers.
 *
 * Centralised so that a date or an amount looks identical on every screen, and
 * so a change of convention is one edit rather than fifty (§56, §57).
 */

/** The admin is English-only today; RTL/Arabic would make this configurable. */
const LOCALE = 'en-GB';

/**
 * `Intl.NumberFormat` construction is comparatively expensive and these are
 * called once per table cell, so instances are cached per currency.
 */
const currencyFormatters = new Map<string, Intl.NumberFormat>();

function currencyFormatter(currency: string, precise: boolean): Intl.NumberFormat {
  const key = precise ? `${currency}:precise` : currency;
  let formatter = currencyFormatters.get(key);

  if (!formatter) {
    const digits = precise ? { minimumFractionDigits: 2, maximumFractionDigits: 4 } : {};
    try {
      formatter = new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
        ...digits,
      });
    } catch {
      // The backend stores CurrencyCode as free text and some rows carry
      // non-ISO values, which makes Intl throw. Fall back to plain decimals
      // rather than crashing a table row.
      formatter = new Intl.NumberFormat(LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: precise ? 4 : 2,
      });
    }
    currencyFormatters.set(key, formatter);
  }

  return formatter;
}

/**
 * Format a monetary amount.
 *
 * The frontend displays money; it does not compute it. Financial arithmetic is
 * the backend's job — doing it here in IEEE-754 floats is how 0.1 + 0.2 ends up
 * on an invoice (§56).
 *
 * `precise` shows up to 4 decimals — for intermediate pricing-engine values
 * (e.g. a price before rounding), which a 2-decimal display would misstate.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency = 'USD',
  options: { precise?: boolean } = {},
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return currencyFormatter(currency, options.precise === true).format(amount);
}

/** A fraction (0.1138…) as a percentage, at most six significant digits (11.3858%). */
export function formatPercent(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) return '—';
  return new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    maximumSignificantDigits: 6,
  }).format(fraction);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(LOCALE).format(value);
}

/**
 * Parse a backend timestamp.
 *
 * Returns null rather than an Invalid Date so callers get one clear failure
 * mode. Note some endpoints return `CreationDate` as a pre-formatted *string*
 * rather than ISO 8601 (e.g. GetOrderReportResponse), which is why the input
 * is permissive.
 */
function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = typeof value === 'string' ? parseISO(value) : value;
  return isValid(date) ? date : null;
}

/** e.g. "21 Sep 2026" */
export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'd MMM yyyy') : '—';
}

/** e.g. "21 Sep 2026, 14:32" */
export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'd MMM yyyy, HH:mm') : '—';
}

/** e.g. "about 3 hours ago" */
export function formatRelativeDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? formatDistanceToNow(date, { addSuffix: true }) : '—';
}

/**
 * Mask a sensitive value, showing only the last few characters.
 *
 * Digital codes are the product — a leaked code is stolen money — so they are
 * masked by default everywhere and revealed only on explicit, permissioned
 * action (§52).
 *
 *   maskSecret('ABCD-EFGH-IJKL-8123') -> '••••-••••-••••-8123'
 */
export function maskSecret(value: string | null | undefined, visibleSuffix = 4): string {
  if (!value) return '—';
  if (value.length <= visibleSuffix) return '•'.repeat(value.length);

  const suffix = value.slice(-visibleSuffix);
  const masked = value.slice(0, -visibleSuffix).replace(/[^-\s]/g, '•');

  return `${masked}${suffix}`;
}
